"use client";
import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  Zoom,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  AccountTree as AccountTreeIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Publish as PublishIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  getAssessmentFormByID,
  updateAssessmentForm,
} from "../../../../api/services/Assessment/AssessmentBuilder";
import ConfirmModal from "../../../ConfirmModal/ConfirmModal";
import { QK } from "../../../../base/qk";
import SignatureFieldBase64 from "../../../CustomForm/SignatureFieldBase64";
import CustomSelect from "../../../FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../../CustomForm/CustomInput";
import { blobToBase64 } from "../../../../utils/blob";

const defaultField = (overrides = {}) => ({
  id: Date.now() + Math.random(),
  label: "",
  field_type: "singleline",
  main_answer_label: "Yes",
  branch_condition: "No",
  branches: [],
  options: [],
  ...overrides,
});

const RenderEditField = ({
  field,
  path,
  sectionIndex,
  level = 0,
  updateField,
  addBranch,
  removeField,
  expanded,
  handleAccordionChange,
}) => {
  const hasBranches = field.branches && field.branches.length > 0;
  const panelId = `panel-${sectionIndex}-${path[0]}`;

  const addOption = () => {
    const newOptions = [
      ...(field.options || []),
      { value: "", order: (field.options?.length || 0) + 1 },
    ];
    updateField(sectionIndex, path, "options", newOptions);
  };

  const removeOption = (optIdx) => {
    const newOptions = field.options.filter((_, i) => i !== optIdx);
    updateField(sectionIndex, path, "options", newOptions);
  };

  const updateOptionValue = (optIdx, val) => {
    const newOptions = field.options.map((opt, i) =>
      i === optIdx ? { ...opt, value: val } : opt
    );
    updateField(sectionIndex, path, "options", newOptions);
  };

  const fieldContent = (
    <Box sx={{ ml: level > 0 ? 2 : 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            flex:
              level === 0
                ? "0 0 calc(66.66% - 8px)"
                : "0 0 calc(83.33% - 8px)",
            minWidth: "200px",
          }}
        >
          <CustomInput
            label={level > 0 ? "Branch Question" : "Main Question"}
            placeholder={
              level > 0
                ? "Enter branch question here..."
                : "Enter main question here..."
            }
            value={field.label || ""}
            onChange={(e) =>
              updateField(sectionIndex, path, "label", e.target.value)
            }
          />
        </Box>

        <Box
          sx={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccountTreeIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => {
              e.stopPropagation();
              addBranch(sectionIndex, path);
            }}
            sx={{
              fontSize: "0.7rem",
              py: 0.3,
              px: 1,
              textTransform: "none",
              minWidth: "auto",
              borderColor: "#2469F0",
              color: "#2469F0",
              "&:hover": {
                bgcolor: "rgba(36, 105, 240, 0.04)",
                borderColor: "#1A5BD4",
              },
            }}
          >
            Branch
          </Button>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              removeField(sectionIndex, path);
            }}
            sx={{
              border: level === 0 ? "1px solid #fee2e2" : "none",
              borderRadius: 1,
              padding: "4px",
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        {hasBranches && (
          <Box sx={{ flex: "0 0 100%" }}>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ p: 1.5, bgcolor: "#F8FAFC", borderRadius: 1 }}
            >
              <CustomInput
                label="Trigger Answer"
                placeholder="e.g. Yes"
                value={field.main_answer_label || ""}
                onChange={(e) =>
                  updateField(
                    sectionIndex,
                    path,
                    "main_answer_label",
                    e.target.value
                  )
                }
                sx={{ flex: 1 }}
              />
              <CustomInput
                label="Condition Logic"
                placeholder="e.g. equals"
                value={field.branch_condition || ""}
                onChange={(e) =>
                  updateField(
                    sectionIndex,
                    path,
                    "branch_condition",
                    e.target.value
                  )
                }
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>
        )}

        {!hasBranches && (
          <Box sx={{ flex: "0 0 calc(50% - 8px)", minWidth: "150px" }}>
            <CustomSelect
              options={[
                { label: "Single Line Text", value: "singleline" },
                { label: "Multi Line Text", value: "multiline" },
                { label: "Select (Dropdown)", value: "select" },
                { label: "Multi-Select Dropdown", value: "multiselect" },
                { label: "Radio Buttons", value: "radio" },
                { label: "Number", value: "number" },
                { label: "Date", value: "date" },
                { label: "Checkbox", value: "checkbox" },
                { label: "Signature", value: "signature" },
              ]}
              value={field.field_type || "singleline"}
              onChange={(e) =>
                updateField(sectionIndex, path, "field_type", e.target.value)
              }
              placeholder="Select Field Type"
              fullWidth
              size="small"
            />
          </Box>
        )}

        {!hasBranches &&
          (field.field_type === "select" ||
            field.field_type === "multiselect" ||
            field.field_type === "radio") && (
            <Box sx={{ flex: "0 0 100%" }}>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  bgcolor: "#FAFBFC",
                  borderRadius: 1,
                  border: "1px solid #EAECF0",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: "#667085",
                    mb: 1.5,
                    display: "block",
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                  }}
                >
                  {field.field_type === "multiselect"
                    ? "Multi-Select Options"
                    : "Options Configuration"}
                </Typography>
                <Stack spacing={1}>
                  {field.options?.map((opt, optIdx) => (
                    <Stack
                      key={optIdx}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <TextField
                        placeholder={`Option ${optIdx + 1}`}
                        size="small"
                        fullWidth
                        value={opt.value}
                        onChange={(e) =>
                          updateOptionValue(optIdx, e.target.value)
                        }
                        sx={{
                          "& .MuiInputBase-input": {
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeOption(optIdx)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={addOption}
                    sx={{
                      justifyContent: "flex-start",
                      fontSize: "0.75rem",
                      color: "#2469F0",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "rgba(36, 105, 240, 0.04)" },
                    }}
                  >
                    Add Option
                  </Button>
                </Stack>
              </Box>
            </Box>
          )}

        {field.branches?.map((branch, bIdx) => (
          <Box sx={{ flex: "0 0 100%" }} key={bIdx}>
            <Box
              sx={{
                mt: 1.5,
                p: 2,
                borderLeft: "3px solid #2469F0",
                bgcolor: "#F8FAFC",
                borderRadius: "0 4px 4px 0",
              }}
            >
              <RenderEditField
                field={branch}
                path={[...path, bIdx]}
                sectionIndex={sectionIndex}
                level={level + 1}
                updateField={updateField}
                addBranch={addBranch}
                removeField={removeField}
                expanded={expanded}
                handleAccordionChange={handleAccordionChange}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  if (level === 0) {
    return (
      <Accordion
        expanded={expanded === panelId}
        onChange={handleAccordionChange(panelId)}
        sx={{
          mb: 1.5,
          border: "1px solid #EAECF0",
          boxShadow: "none",
          borderRadius: "8px !important",
          "&:before": { display: "none" },
          overflow: "hidden",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
          sx={{
            bgcolor: "#F8FAFC",
            minHeight: "40px",
            "& .MuiAccordionSummary-content": {
              margin: "6px 0",
            },
          }}
        >
          <Typography
            sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#344054" }}
          >
            {field.label || `Question ${path[0] + 1}`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 2.5, bgcolor: "#FFFFFF" }}>
          {fieldContent}
        </AccordionDetails>
      </Accordion>
    );
  }

  return fieldContent;
};

const AssessmentFormEdit = ({ formId, refetch }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    isActive: false,
  });
  const [managerSignature, setManagerSignature] = useState(null);

  const { isLoading } = useQuery(
    [QK.assessmentForm, formId],
    () => getAssessmentFormByID(formId),
    {
      enabled: !!formId,
      onSuccess: (data) => {
        const normalizedSections = data.sections.map((s) => ({
          ...s,
          fields: s.section_fields || s.fields || [],
        }));
        setFormData({ ...data, sections: normalizedSections });
      },
    }
  );

  const mutation = useMutation(
    (updated) => updateAssessmentForm(formId, updated),
    {
      onSuccess: () => {
        enqueueSnackbar(
          confirmModal.isActive
            ? "Template Updated & Published!"
            : "Draft Updated!",
          { variant: "success" }
        );
        setConfirmModal({ show: false, isActive: false });
        setManagerSignature(null);
        queryClient.invalidateQueries([QK.assessmentForms]);
        queryClient.invalidateQueries([QK.assessmentForm, formId]);
        if (refetch) refetch();
      },
      onError: () =>
        enqueueSnackbar("Failed to update template", { variant: "error" }),
    }
  );

  const handleAccordionChange = (panel) => (event, isExpanded) =>
    setExpanded(isExpanded ? panel : false);

  const handleConfirmAction = async () => {
    if (!managerSignature) {
      enqueueSnackbar("Manager signature is mandatory.", { variant: "error" });
      return;
    }

    try {
      const signatureBase64 = await blobToBase64(managerSignature);
      const payload = {
        ...formData,
        is_active: confirmModal.isActive,
        manager_signature: signatureBase64,
      };
      mutation.mutate(payload);
    } catch (error) {
      enqueueSnackbar("Error processing signature", { variant: "error" });
    }
  };

  const updateField = (sIdx, path, key, val) => {
    const newSections = JSON.parse(JSON.stringify(formData.sections));
    const findAndSet = (fields, p) => {
      const [idx, ...rest] = p;
      if (rest.length === 0) fields[idx][key] = val;
      else findAndSet(fields[idx].branches, rest);
    };
    findAndSet(newSections[sIdx].fields, path);
    setFormData({ ...formData, sections: newSections });
  };

  const addBranch = (sIdx, path) => {
    const newSections = JSON.parse(JSON.stringify(formData.sections));
    const findAndAdd = (fields, p) => {
      const [idx, ...rest] = p;
      if (rest.length === 0) {
        fields[idx].branches = [
          ...(fields[idx].branches || []),
          defaultField({ field_type: "multiline" }),
        ];
        if (fields[idx].field_type === "singleline") {
          fields[idx].field_type = "select";
        }
      } else findAndAdd(fields[idx].branches, rest);
    };
    findAndAdd(newSections[sIdx].fields, path);
    setFormData({ ...formData, sections: newSections });
    setExpanded(`panel-${sIdx}-${path[0]}`);
  };

  const removeField = (sIdx, path) => {
    const newSections = JSON.parse(JSON.stringify(formData.sections));
    const findAndRem = (fields, p) => {
      const [idx, ...rest] = p;
      if (rest.length === 0) fields.splice(idx, 1);
      else findAndRem(fields[idx].branches, rest);
    };
    findAndRem(newSections[sIdx].fields, path);
    setFormData({ ...formData, sections: newSections });
  };

  if (isLoading || !formData)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ width: "100%", position: "relative", pb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: "0.95rem",
            fontWeight: 600,
            color: "#101828",
            letterSpacing: "-0.01em",
          }}
        >
          Edit Assessment Form
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "#667085", mt: 0.2 }}>
          Modify dynamic template questions and branching logic
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomInput
          label="Assessment Title"
          placeholder="Enter assessment form title..."
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
      </Box>

      {formData.sections.map((section, sIdx) => (
        <Box key={sIdx} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
            <TextField
              variant="standard"
              fullWidth
              placeholder="Enter section name..."
              value={section.title}
              onChange={(e) => {
                const ns = [...formData.sections];
                ns[sIdx].title = e.target.value;
                setFormData({ ...formData, sections: ns });
              }}
              sx={{
                flexGrow: 1,
                "& input": {
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#101828",
                },
                "& input::placeholder": {
                  fontSize: "0.8rem",
                  color: "#98A2B3",
                },
              }}
            />
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                const ns = formData.sections.filter((_, i) => i !== sIdx);
                setFormData({ ...formData, sections: ns });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>

          {section.fields.map((field, fIdx) => (
            <RenderEditField
              key={fIdx}
              field={field}
              path={[fIdx]}
              sectionIndex={sIdx}
              updateField={updateField}
              addBranch={addBranch}
              removeField={removeField}
              expanded={expanded}
              handleAccordionChange={handleAccordionChange}
            />
          ))}

          <Button
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
            fullWidth
            variant="text"
            sx={{
              mt: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              color: "#2469F0",
              justifyContent: "flex-start",
              "&:hover": { bgcolor: "rgba(36, 105, 240, 0.04)" },
            }}
            onClick={() => {
              const ns = [...formData.sections];
              ns[sIdx].fields.push(defaultField());
              setFormData({ ...formData, sections: ns });
            }}
          >
            + Add Question
          </Button>
        </Box>
      ))}

      <Button
        variant="text"
        fullWidth
        sx={{
          py: 2,
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.85rem",
          color: "#2469F0",
          border: "2px dashed #EAECF0",
          "&:hover": {
            borderColor: "#2469F0",
            bgcolor: "rgba(36, 105, 240, 0.02)",
          },
        }}
        onClick={() =>
          setFormData({
            ...formData,
            sections: [
              ...formData.sections,
              {
                id: Date.now(),
                title: "New Section",
                fields: [defaultField()],
              },
            ],
          })
        }
      >
        + Add New Section
      </Button>

      <Box
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          zIndex: 1000,
        }}
      >
        <Zoom in={true}>
          <Tooltip title="Save Update" placement="left" arrow>
            <Fab
              onClick={() =>
                setConfirmModal({ show: true, isActive: false })
              }
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                color: "#667085",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                width: 48,
                height: 48,
                "&:hover": { bgcolor: "#FFFFFF" },
              }}
            >
              <SaveIcon fontSize="small" />
            </Fab>
          </Tooltip>
        </Zoom>

        <Zoom in={true}>
          <Tooltip title="Update & Publish" placement="left" arrow>
            <Fab
              variant="extended"
              onClick={() => setConfirmModal({ show: true, isActive: true })}
              sx={{
                px: 2.5,
                bgcolor: "#2469F0",
                color: "#FFFFFF",
                boxShadow: "0 10px 20px rgba(36, 105, 240, 0.3)",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 48,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#1A5BD4",
                  boxShadow: "0 15px 25px rgba(36, 105, 240, 0.4)",
                },
              }}
            >
              <PublishIcon sx={{ mr: 1, fontSize: 18 }} />
              Update & Publish
            </Fab>
          </Tooltip>
        </Zoom>
      </Box>

      <ConfirmModal
        show={confirmModal.show}
        handleClose={() => {
          setConfirmModal({ show: false, isActive: false });
          setManagerSignature(null);
        }}
        handleConfirm={handleConfirmAction}
        isLoading={mutation.isLoading}
        title={
          confirmModal.isActive
            ? "Update & Publish Assessment"
            : "Update Draft"
        }
        body={
          <Box>
            <Typography sx={{ mb: 2, fontSize: "0.85rem", color: "#344054" }}>
              {confirmModal.isActive
                ? "Please sign below to authorize and publish these updates."
                : "A manager's signature is required to update this draft."}
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "#EAECF0" }} />
            <Box
              sx={{
                p: 2,
                bgcolor: "#F8FAFC",
                borderRadius: "8px",
                border: "1px solid #EAECF0",
              }}
            >
              <SignatureFieldBase64
                value={managerSignature}
                onChange={(val) => setManagerSignature(val)}
                label="Manager Authorization Signature"
              />
            </Box>
          </Box>
        }
        confirmText={confirmModal.isActive ? "Update Now" : "Update Draft"}
      />
    </Box>
  );
};

export default AssessmentFormEdit;